import clsx from 'clsx';
import * as React from 'react';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Button, SelectButton } from '../Button';
import { Menu, MenuItem, MenuRoot, MenuTrigger } from '../Menu';
import { Popover } from '../Popover';

/** Page sizes offered by the Pagination items-per-page selector. */
const PAGINATION_ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

export type PaginationRangeProps = {
    /** Label for the range of items being displayed */
    label?: string;
    /** Total number of items in the list */
    totalItems: number;
    /** Number of items displayed per page */
    itemsPerPage: number;
    /** Index of the current page */
    page: number;
};

export type PaginationNavigationCallbacks = {
    /** Function to call when the user clicks the Next button. */
    next: (() => void | Promise<void>) | undefined;
    /** Function to call when the user clicks the Previous button. */
    previous: (() => void | Promise<void>) | undefined;
};

export type PaginationNavigationProps = PaginationNavigationCallbacks & {
    className?: string;
};

export type PaginationProps = PaginationNavigationProps & {
    /**
     * Info about the range of items being displayed
     */
    range?: PaginationRangeProps;
    /**
     * Called when the user picks a new page size. Requires `range`, which controls the select value.
     */
    onItemsPerPageChange?: (itemsPerPage: number) => void;
};

export const PaginationContext = React.createContext<
    ContextValue<Partial<PaginationProps>, HTMLDivElement>
>({});

/**
 * Footer for a paginated list. Displays optional range context, an optional items-per-page selector,
 * and Previous/Next navigation controls.
 */
export function Pagination({
    ref,
    ...props
}: PaginationProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, PaginationContext);
    const { className, range, onItemsPerPageChange, ...actionProps } = props;

    if (!range) {
        return <PaginationNavigation ref={ref} {...actionProps} className={className} />;
    }

    if (
        range.totalItems <= 1 &&
        !actionProps.next &&
        !actionProps.previous &&
        !onItemsPerPageChange
    ) {
        return null;
    }

    return (
        <div ref={ref} className={clsx('flex items-center justify-between gap-2', className)}>
            <div className="flex items-center gap-3">
                {onItemsPerPageChange ? (
                    <PaginationItemsPerPageSelect
                        itemsPerPage={range.itemsPerPage}
                        onItemsPerPageChange={onItemsPerPageChange}
                    />
                ) : null}
                {range.totalItems > 1 ? <PaginationRange {...range} /> : null}
            </div>
            <PaginationNavigation {...actionProps} />
        </div>
    );
}

const PaginationNavigation = function PaginationNavigation({
    ref,
    ...props
}: PaginationNavigationProps & {
    ref?: React.ForwardedRef<HTMLDivElement>;
}): React.ReactElement | null {
    const { className, next, previous } = props;

    return next || previous ? (
        <nav
            ref={ref}
            aria-label="Pagination Navigation"
            className={clsx('flex justify-end gap-2', className)}
        >
            {previous ? (
                <Button
                    leadingIcon={Icon.ChevronLeft}
                    kind="blank"
                    size="small"
                    onPress={async () => {
                        await previous();
                    }}
                    label="Previous"
                />
            ) : null}
            {next ? (
                <Button
                    trailingIcon={Icon.ChevronRight}
                    kind="blank"
                    size="small"
                    onPress={async () => {
                        await next();
                    }}
                    label="Next"
                />
            ) : null}
        </nav>
    ) : null;
};

function PaginationRange(props: PaginationRangeProps): React.ReactElement {
    const { totalItems, itemsPerPage, page, label = 'items' } = props;

    const start = page * itemsPerPage + 1;
    const end = Math.min((page + 1) * itemsPerPage, totalItems);

    return (
        <div aria-live="polite" className="text-ui-small text-muted">
            {start} - {end} of {totalItems} {label}
        </div>
    );
}

function PaginationItemsPerPageSelect(props: {
    itemsPerPage: number;
    onItemsPerPageChange: (itemsPerPage: number) => void;
}): React.ReactElement {
    const { itemsPerPage, onItemsPerPageChange } = props;

    return (
        <div className="flex items-center gap-1 text-ui-small text-muted">
            <MenuRoot>
                <MenuTrigger>
                    <SelectButton
                        aria-label={`${itemsPerPage} items per page`}
                        className="h-7 min-w-14 text-ui-small"
                    >
                        {itemsPerPage}
                    </SelectButton>
                </MenuTrigger>
                <Popover placement="bottom-start">
                    <Menu>
                        {PAGINATION_ITEMS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem
                                key={option}
                                textValue={String(option)}
                                checked={option === itemsPerPage}
                                onAction={() => onItemsPerPageChange(option)}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </Menu>
                </Popover>
            </MenuRoot>
            <span>per page</span>
        </div>
    );
}
